import React, { useEffect, useRef, useState } from "react";
import { FiMusic, FiPlay, FiPause, FiVolume2, FiX } from "react-icons/fi";
import "./music.css";

// The playlist is data-driven, just like the jobs list — you add music by editing
// a JSON manifest, not this component. To add a track for everyone:
//   1. Drop an .mp3 into public/music/
//   2. Add an entry { "name": "...", "file": "..." } to public/music/playlist.json
//   3. Commit + push. The player picks it up automatically on next load.
// A second, git-ignored public/music/playlist.local.json can list personal or
// unlicensed tracks that play on your machine only and are never published. Both
// manifests are fetched and merged (de-duplicated by file name); the local one is
// optional and silently skipped when absent (e.g. on a fresh clone or deploy).

// Used only if neither manifest can be loaded, so the widget is never left empty.
const FALLBACK_TRACKS = [{ name: "2 AM Debug Loop", file: "lofi-debug-loop.mp3" }];

// Resolve a public/music/<file> URL, honouring CRA's PUBLIC_URL / homepage base.
function musicUrl(file) {
  const base = process.env.PUBLIC_URL || "";
  return `${base}/music/${encodeURIComponent(file)}`;
}

// Load a track manifest from public/music/. Accepts either a bare array or an
// object with a "tracks" array. Returns [] on any error (missing file, bad JSON)
// so an absent optional manifest simply contributes nothing to the playlist.
async function loadManifest(file, local) {
  try {
    const res = await fetch(musicUrl(file), { cache: "no-cache" });
    if (!res.ok) return [];
    const data = await res.json();
    const list = Array.isArray(data) ? data : data && data.tracks;
    if (!Array.isArray(list)) return [];
    return list
      .filter((t) => t && typeof t.file === "string" && t.file.trim())
      .map((t) => ({ name: (t.name && String(t.name)) || t.file, file: t.file, local: !!local }));
  } catch {
    return [];
  }
}

// Global background music player — a floating widget that sits above the feedback
// button and keeps playing across every page (it is mounted once, outside the
// route switch, so navigation never unmounts the <audio> element). The track list
// is loaded from JSON manifests at runtime; a missing file degrades to a small
// hint instead of crashing.
export default function LofiWidget() {
  const audioRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [track, setTrack] = useState(0);
  const [volume, setVolume] = useState(0.6);
  const [missing, setMissing] = useState(false);

  // Load the committed playlist plus the optional local-only playlist once, then
  // merge and de-duplicate by file name so a track is never listed twice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [shared, local] = await Promise.all([
        loadManifest("playlist.json", false),
        loadManifest("playlist.local.json", true),
      ]);
      const seen = new Set();
      const merged = [];
      [...shared, ...local].forEach((t) => {
        const key = t.file.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(t);
        }
      });
      if (!cancelled) {
        setTracks(merged.length ? merged : FALLBACK_TRACKS.map((t) => ({ ...t, local: false })));
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Point the <audio> element at the given track's file if it is not already.
  function ensureSrc(el, i) {
    const t = tracks[i];
    if (!t) return;
    const url = musicUrl(t.file);
    if (el.getAttribute("src") !== url) el.src = url;
  }

  function toggle() {
    const el = audioRef.current;
    if (!el || !tracks.length) return;
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    ensureSrc(el, track);
    el.play()
      .then(() => {
        setPlaying(true);
        setMissing(false);
      })
      .catch(() => {
        setPlaying(false);
        setMissing(true);
      });
  }

  function chooseTrack(i) {
    setTrack(i);
    setMissing(false);
    const el = audioRef.current;
    if (!el) return;
    ensureSrc(el, i);
    if (playing) {
      el.play()
        .then(() => setMissing(false))
        .catch(() => {
          setPlaying(false);
          setMissing(true);
        });
    }
  }

  function handleError() {
    setPlaying(false);
    setMissing(true);
  }

  const current = tracks[track];

  return (
    <div className="lw-root">
      <audio ref={audioRef} loop preload="none" onError={handleError} />

      {open ? (
        <div className="lw-panel" role="dialog" aria-label="Background music">
          <div className="lw-head">
            <span className="lw-title"><FiMusic size={14} /> Focus music</span>
            <button type="button" className="lw-x" onClick={() => setOpen(false)} aria-label="Close">
              <FiX size={16} />
            </button>
          </div>

          <button type="button" className="nb-btn nb-btn-sm lw-play" onClick={toggle} disabled={!tracks.length}>
            {playing ? <FiPause size={14} /> : <FiPlay size={14} />} {playing ? "Pause" : "Play"}
          </button>

          <div className="lw-stations">
            {tracks.map((t, i) => (
              <button
                key={t.file}
                type="button"
                className={`lw-station ${i === track ? "active" : ""}`}
                onClick={() => chooseTrack(i)}
              >
                {t.name}
              </button>
            ))}
          </div>

          {!loaded ? (
            <div className="lw-now"><span className="lw-now-text">Loading playlist…</span></div>
          ) : missing && current ? (
            <div className="lw-missing">
              {current.local ? "Local-only track — add " : "Add "}
              <code>{current.file}</code> to <code>public/music/</code> to play it.
            </div>
          ) : current ? (
            <div className="lw-now" title={current.name}>
              <span className={`lw-now-dot ${playing ? "on" : ""}`} aria-hidden="true" />
              <span className="lw-now-text">{playing ? "Now playing" : "Selected"}: {current.name}</span>
            </div>
          ) : (
            <div className="lw-missing">
              No tracks yet — add one to <code>public/music/playlist.json</code>.
            </div>
          )}

          <div className="lw-vol">
            <FiVolume2 size={14} />
            <input
              type="range"
              className="lw-vol-slider"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              aria-label="Music volume"
            />
          </div>

          <span className="lw-credit">Loops · CC0 lofi (Open Lo-Fi) · add your own in playlist.json</span>
        </div>
      ) : null}

      <button
        type="button"
        className={`lw-fab ${playing ? "playing" : ""} ${open ? "open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Background music"
        title={playing ? "Music playing" : "Focus music"}
      >
        {playing ? <span className="lw-fab-eq" aria-hidden="true"><i /><i /><i /></span> : <FiMusic size={20} />}
      </button>
    </div>
  );
}
